import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  loadState, saveState, resetState as doReset,
  appendHistory, upsertTodayHistory,
  countSets, countSetsForDay,
  loadTrainingDates, recordTrainingDate, computeStreak,
} from "@/storage/routine";
import { DEFAULT_IMAGE, newExerciseId } from "@/data/defaultRoutine";
import { archiveWeek, carryOverProgress } from "@/storage/weeks";
import { useAuth } from "@/context/AuthContext";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [activeDay, setActiveDayRaw] = useState(0);
  const [nameSheetOpen, setNameSheetOpen] = useState(null);
  const [trainingDates, setTrainingDates] = useState([]);
  const [restTimer, setRestTimer] = useState({ active: false, remaining: 0, total: 90, key: 0 });
  const timerRef = useRef(null);

  const setActiveDay = useCallback((idx) => {
    setActiveDayRaw(idx);
    if (user?.email) {
      sessionStorage.setItem(`tryad_activeDay_${user.email}`, String(idx));
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const s = loadState();
    setState(s);
    const dates = loadTrainingDates();
    setTrainingDates(dates);
    const saved = sessionStorage.getItem(`tryad_activeDay_${user.email}`);
    const dayIdx = saved !== null ? parseInt(saved, 10) : 0;
    // Sin días, routine.length - 1 es -1: hay que quedarse en 0 y no en un
    // índice negativo, aunque no haya nada que mostrar todavía.
    setActiveDayRaw(Math.max(0, Math.min(dayIdx, s.routine.length - 1)));
    if (!s.userName) setNameSheetOpen("onboarding");
    else setNameSheetOpen(null);
  }, [user]);

  // Rest timer countdown — only re-runs when active state or key changes
  useEffect(() => {
    if (!restTimer.active) return;
    timerRef.current = setInterval(() => {
      setRestTimer((t) => {
        if (t.remaining <= 1) {
          clearInterval(timerRef.current);
          return { ...t, active: false, remaining: 0 };
        }
        return { ...t, remaining: t.remaining - 1 };
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [restTimer.active, restTimer.key]);

  const startRestTimer = useCallback((seconds = 90) => {
    clearInterval(timerRef.current);
    setRestTimer((t) => ({ active: true, remaining: seconds, total: seconds, key: t.key + 1 }));
  }, []);

  const cancelRestTimer = useCallback(() => {
    clearInterval(timerRef.current);
    setRestTimer((t) => ({ ...t, active: false, remaining: 0 }));
  }, []);

  const persist = useCallback((next) => {
    setState(next);
    saveState(next);
  }, []);

  const totals = useMemo(() => {
    if (!state) return { total: 0, done: 0 };
    return countSets(state.routine, state.progress);
  }, [state]);

  const daily = useMemo(() => {
    if (!state) return { total: 0, done: 0 };
    return countSetsForDay(state.routine, state.progress, activeDay);
  }, [state, activeDay]);

  const progress = totals.total > 0 ? totals.done / totals.total : 0;
  const dailyProgress = daily.total > 0 ? daily.done / daily.total : 0;
  const streak = useMemo(() => computeStreak(trainingDates), [trainingDates]);

  // El ciclo se cierra cuando no queda ninguna serie sin marcar en ningún día.
  const cycleComplete = totals.total > 0 && totals.done === totals.total;

  // Archiva el ciclo y arranca el siguiente conservando pesos y repeticiones.
  const startNextWeek = useCallback(() => {
    if (!state) return;
    archiveWeek({
      week: state.week || 1,
      routine: state.routine,
      progress: state.progress,
      alternatives: state.alternatives,
      startedAt: state.weekStartedAt,
    });
    persist({
      ...state,
      progress: carryOverProgress(state.progress),
      week: (state.week || 1) + 1,
      weekStartedAt: new Date().toISOString(),
    });
  }, [state, persist]);

  const getAlternative = useCallback((exId) => {
    if (!state) return 0;
    return state.alternatives[`${activeDay}-${exId}`] || 0;
  }, [state, activeDay]);

  const updateAlternative = useCallback((exId, altIdx) => {
    if (!state) return;
    persist({ ...state, alternatives: { ...state.alternatives, [`${activeDay}-${exId}`]: altIdx } });
  }, [state, activeDay, persist]);

  const toggleSet = useCallback((exId, setIdx) => {
    if (!state) return false;
    const key = `${activeDay}-${exId}-${setIdx}`;
    const current = state.progress[key] || { done: false, weight: 0, reps: 0 };
    const nextDone = !current.done;
    persist({
      ...state,
      progress: {
        ...state.progress,
        [key]: { ...current, done: nextDone, completedAt: nextDone ? new Date().toISOString() : null },
      },
    });
    if (nextDone && current.weight > 0) {
      const ex = state.routine[activeDay].exercises.find((e) => e.id === exId);
      if (ex) {
        const parts = ex.name.split(" / ").map((s) => s.trim()).filter(Boolean);
        const altIdx = state.alternatives[`${activeDay}-${exId}`] || 0;
        const historyName = parts[altIdx] || ex.name;
        appendHistory(historyName, current.weight);
      }
      recordTrainingDate();
      setTrainingDates(loadTrainingDates());
    }
    return nextDone;
  }, [state, activeDay, persist]);

  const updateWeight = useCallback((exId, setIdx, weight) => {
    if (!state) return;
    const key = `${activeDay}-${exId}-${setIdx}`;
    const current = state.progress[key] || { done: false, weight: 0, reps: 0 };
    persist({ ...state, progress: { ...state.progress, [key]: { ...current, weight } } });
    // Update history if set is already done and weight changed
    if (current.done && weight > 0) {
      const ex = state.routine[activeDay].exercises.find((e) => e.id === exId);
      if (ex) {
        const parts = ex.name.split(" / ").map((s) => s.trim()).filter(Boolean);
        const altIdx = state.alternatives[`${activeDay}-${exId}`] || 0;
        const historyName = parts[altIdx] || ex.name;
        upsertTodayHistory(historyName, weight);
      }
    }
  }, [state, activeDay, persist]);

  const updateReps = useCallback((exId, setIdx, reps) => {
    if (!state) return;
    const key = `${activeDay}-${exId}-${setIdx}`;
    const current = state.progress[key] || { done: false, weight: 0, reps: 0 };
    persist({ ...state, progress: { ...state.progress, [key]: { ...current, reps } } });
  }, [state, activeDay, persist]);

  const deleteExercise = useCallback((exId) => {
    if (!state) return;
    const routine = state.routine.map((d, i) =>
      i === activeDay ? { ...d, exercises: d.exercises.filter((e) => e.id !== exId) } : d
    );
    const newProgress = { ...state.progress };
    const newAlts = { ...state.alternatives };
    Object.keys(newProgress).forEach((k) => { if (k.includes(`-${exId}-`)) delete newProgress[k]; });
    delete newAlts[`${activeDay}-${exId}`];
    persist({ ...state, routine, progress: newProgress, alternatives: newAlts });
  }, [state, activeDay, persist]);

  const saveExercise = useCallback((data) => {
    if (!state) return;
    const routine = state.routine.map((d, i) => {
      if (i !== activeDay) return d;
      if (data.id) {
        return {
          ...d,
          exercises: d.exercises.map((e) =>
            e.id === data.id
              ? {
                  ...e,
                  name: data.name,
                  muscleGroup: data.muscleGroup,
                  sets: data.sets,
                  reps: data.reps,
                  // Sustituir un ejercicio por otro de la biblioteca trae
                  // animación e id nuevos; editarlo a mano no los toca.
                  ...(data.img ? { img: data.img } : {}),
                  ...(data.libraryId ? { libraryId: data.libraryId } : {}),
                }
              : e
          ),
        };
      }
      return {
        ...d,
        exercises: [...d.exercises, {
          id: newExerciseId(),
          name: data.name,
          muscleGroup: data.muscleGroup,
          sets: data.sets,
          reps: data.reps,
          // Los de la biblioteca vienen con su GIF; los creados a mano no
          // tienen imagen hasta que el usuario le ponga una.
          img: data.img || DEFAULT_IMAGE,
          libraryId: data.libraryId,
        }],
      };
    });
    persist({ ...state, routine });
  }, [state, activeDay, persist]);

  // Reordenar es solo mover elementos del array: el progreso y las variantes
  // se guardan por id de ejercicio, no por posición, así que no hay nada que
  // reindexar (a diferencia de reorderDays, que sí lo necesita).
  const reorderExercises = useCallback((orderedIds) => {
    if (!state) return;
    const routine = state.routine.map((d, i) => {
      if (i !== activeDay) return d;
      const byId = new Map(d.exercises.map((e) => [e.id, e]));
      const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean);
      // Cualquier ejercicio que no viniera en la lista se conserva al final,
      // para que un desajuste nunca borre ejercicios.
      const missing = d.exercises.filter((e) => !orderedIds.includes(e.id));
      return { ...d, exercises: [...reordered, ...missing] };
    });
    persist({ ...state, routine });
  }, [state, activeDay, persist]);

  const updateImage = useCallback((exId, uri) => {
    if (!state) return;
    const routine = state.routine.map((d, i) =>
      i === activeDay
        ? { ...d, exercises: d.exercises.map((e) => (e.id === exId ? { ...e, img: uri } : e)) }
        : d
    );
    persist({ ...state, routine });
  }, [state, activeDay, persist]);

  const saveName = useCallback((newName) => {
    const current = state || { routine: [], progress: {}, alternatives: {}, userName: "" };
    persist({ ...current, userName: newName });
    setNameSheetOpen(null);
  }, [state, persist]);

  const handleReset = useCallback(() => {
    if (!state) return;
    setState(doReset(state.userName));
  }, [state]);

  const addDay = useCallback((name, short) => {
    if (!state) return;
    const routine = [...state.routine, { name, short, exercises: [] }];
    persist({ ...state, routine });
    setActiveDay(routine.length - 1);
  }, [state, persist, setActiveDay]);

  const editDay = useCallback((dayIdx, name, short) => {
    if (!state) return;
    const routine = state.routine.map((d, i) => (i === dayIdx ? { ...d, name, short } : d));
    persist({ ...state, routine });
  }, [state, persist]);

  const deleteDay = useCallback((dayIdx) => {
    if (!state || state.routine.length <= 1) return;
    const routine = state.routine.filter((_, i) => i !== dayIdx);
    const newProgress = {};
    const newAlts = {};
    Object.entries(state.progress).forEach(([key, val]) => {
      const parts = key.split("-");
      const dIdx = parseInt(parts[0], 10);
      if (dIdx === dayIdx) return;
      const newDIdx = dIdx > dayIdx ? dIdx - 1 : dIdx;
      newProgress[`${newDIdx}-${parts.slice(1).join("-")}`] = val;
    });
    Object.entries(state.alternatives || {}).forEach(([key, val]) => {
      const parts = key.split("-");
      const dIdx = parseInt(parts[0], 10);
      if (dIdx === dayIdx) return;
      const newDIdx = dIdx > dayIdx ? dIdx - 1 : dIdx;
      newAlts[`${newDIdx}-${parts.slice(1).join("-")}`] = val;
    });
    const newActive = Math.max(0, activeDay >= routine.length ? routine.length - 1 : activeDay > dayIdx ? activeDay - 1 : activeDay);
    setActiveDay(newActive);
    persist({ ...state, routine, progress: newProgress, alternatives: newAlts });
  }, [state, activeDay, persist, setActiveDay]);

  const reorderDays = useCallback((fromIdx, toIdx) => {
    if (!state || fromIdx === toIdx) return;
    const routine = [...state.routine];
    const [moved] = routine.splice(fromIdx, 1);
    routine.splice(toIdx, 0, moved);

    // Build old-index → new-index mapping
    const indexMap = {};
    const order = state.routine.map((_, i) => i);
    const [m] = order.splice(fromIdx, 1);
    order.splice(toIdx, 0, m);
    order.forEach((oldIdx, newIdx) => { indexMap[oldIdx] = newIdx; });

    const newProgress = {};
    Object.entries(state.progress).forEach(([key, val]) => {
      const parts = key.split("-");
      const dIdx = parseInt(parts[0], 10);
      newProgress[`${indexMap[dIdx] ?? dIdx}-${parts.slice(1).join("-")}`] = val;
    });
    const newAlts = {};
    Object.entries(state.alternatives || {}).forEach(([key, val]) => {
      const parts = key.split("-");
      const dIdx = parseInt(parts[0], 10);
      newAlts[`${indexMap[dIdx] ?? dIdx}-${parts.slice(1).join("-")}`] = val;
    });

    setActiveDay(indexMap[activeDay] ?? activeDay);
    persist({ ...state, routine, progress: newProgress, alternatives: newAlts });
  }, [state, activeDay, persist, setActiveDay]);

  const value = useMemo(() => ({
    state, activeDay, setActiveDay,
    totals, daily, progress, dailyProgress, streak,
    cycleComplete, startNextWeek,
    getAlternative, updateAlternative,
    toggleSet, updateWeight, updateReps,
    deleteExercise, saveExercise, updateImage, reorderExercises,
    saveName, handleReset,
    addDay, editDay, deleteDay, reorderDays,
    nameSheetOpen, setNameSheetOpen,
    restTimer, startRestTimer, cancelRestTimer,
  }), [
    state, activeDay, setActiveDay, totals, daily, progress, dailyProgress, streak,
    cycleComplete, startNextWeek,
    getAlternative, updateAlternative,
    toggleSet, updateWeight, updateReps,
    deleteExercise, saveExercise, updateImage, reorderExercises,
    saveName, handleReset,
    addDay, editDay, deleteDay, reorderDays,
    nameSheetOpen, setNameSheetOpen,
    restTimer, startRestTimer, cancelRestTimer,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
